import { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { API, AuthContext } from '../App';
import axios from '../lib/apiTransport';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      toast.error('Invalid reset link');
      navigate('/');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 12) {
      toast.error('Password must be at least 12 characters long');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });
      
      // Auto-login with returned tokens
      if (response.data.access_token && response.data.refresh_token && response.data.user) {
        login(response.data.access_token, response.data.refresh_token, response.data.user);
        toast.success('Password reset successfully! Welcome back.');
        navigate('/dashboard');
      } else {
        // Fallback if tokens not returned (shouldn't happen)
        toast.success('Password reset successfully! Please log in.');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <h1 className="text-3xl font-bold text-center mb-6 text-gray-900">Reset Password</h1>
        <p className="text-sm text-gray-600 text-center mb-6">
          Enter your new password below
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 12 characters"
              required
              minLength={12}
              data-testid="new-password-input"
            />
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              required
              minLength={12}
              data-testid="confirm-password-input"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full btn-primary" 
            disabled={isLoading}
            data-testid="reset-password-submit"
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>

          <Button 
            type="button" 
            variant="outline" 
            className="w-full" 
            onClick={() => navigate('/')}
            data-testid="back-to-login"
          >
            Back to Login
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default ResetPassword;
